import React, { useEffect, useMemo } from "react";

import useMeasure from "react-use-measure";
import { LearningRecordCourse } from "src/types/coaching/learners";
import DOMPurify from "dompurify";
import { CourseContext } from "../../../context";
import { AssetViewer } from "src/pages/asset/components/assetViewerEx";
import EmptyMessage from "src/components/emptyMessage";
import Text from "src/components/text";

import _ from "lodash";
import cx from 'classnames';
import styles from "./lesson.module.scss";

interface ViewLessonContentProps {
  course: LearningRecordCourse;
  lesson: any;
  collapsed: boolean;
}

export const ViewLessonContent: React.FC<ViewLessonContentProps> = ({ lesson, collapsed }) => {
  const [descriptionRef, bounds] = useMeasure();
  const [assetViewerRef, assetViewerBounds] = useMeasure();

  const courseContext = React.useContext(CourseContext);

  const name = useMemo(() => {
    return _.get(lesson, "name");
  }, [lesson]);

  const description = useMemo(() => {
    return _.get(lesson, "description");
  }, [lesson]);

  const assetId = useMemo(() => {
    return _.get(lesson, "content.id");
  }, [lesson]);

  const asset = useMemo(() => {
    return courseContext.asset?.item;
  }, [courseContext.asset]);

  const { lessonSession, analyticsSession } = useMemo(() => {
    const lessonSession = _.get(courseContext.courseContentSession, "session");
    const analyticsSession = _.get(courseContext.courseContentSession, "analyticsSession");

    return {
      lessonSession,
      analyticsSession,
    };
  }, [courseContext.courseContentSession]);

  useEffect(() => {
    if (assetId) {
      if (courseContext.fetchLesson) {
        courseContext.fetchLesson(lesson);
      }
    }
  }, [assetId]);

  const handleFetchPreviewData = () => {
    if (lesson) {
      if (courseContext.fetchLessonPreview) {
        courseContext.fetchLessonPreview(lesson);
      }
    }
  }

  const recordSessionAnalytics = (data) => {
    if (!lessonSession && courseContext.createCourseContentSession) {
      courseContext.createCourseContentSession(lesson);
    }

    if (!analyticsSession && courseContext.createLessonAnalyticsEvent) {
      courseContext.createLessonAnalyticsEvent(lesson);
    }

    if (analyticsSession && courseContext.recordLessonAnalyticsEvent) {
      courseContext.recordLessonAnalyticsEvent?.(lesson, analyticsSession, data);
    }
  };

  const handleAnalyticsChange = (data) => {
    const progress = data.percentageRead || 0;
    const duration = data.duration;

    if (courseContext.updateCourseContentSession && lessonSession) {
      courseContext.updateCourseContentSession(lesson, lessonSession, duration, progress);
    }
  }

  return (
    <div className={cx(styles.viewContainer, {
      [styles.collapsed]: collapsed
    })}>
      <div className={styles.header}>
        <Text className={styles.name} text={name} lines={3} />
      </div>
      <div ref={descriptionRef} className={styles.description} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
      <div ref={assetViewerRef} className={styles.assetContainer}>
        {courseContext.asset?.loading && !asset && (
          <div className={styles.loading}>
            <img src={'/images/loading_plane.gif'} />
            <div className={styles.message}>Loading lesson...</div>
          </div>
        )}

        {!courseContext.asset?.loading && asset && (
          <AssetViewer
            key={asset.id}
            className={styles.assetViewer}
            asset={asset}
            offset={assetViewerBounds.top + 20}
            preferredWidth={assetViewerBounds.width || 1024}
            scrollParent={`.${styles.viewContainer}`}

            preview={{
              loading: courseContext.asset?.loading || false,
              item: courseContext.asset?.preview || null,
            }}

            fetchPreviewData={handleFetchPreviewData}

            recordSessionAnalytics={recordSessionAnalytics}
            onAnalyticsChange={handleAnalyticsChange}
          />
        )}

        {!courseContext.asset?.loading && !asset && (
          <div className={styles.notFoundContainer}>
            <EmptyMessage
              width={500}
              image="/images/assets/404error_message.svg"
              heading="Mmm... who moved my cheese?"
              message="Sorry!  Some one passed you the wrong information. You were never supposed to see me :). This asset does not exist any longer."
            />
          </div>
        )}
      </div>
    </div>
  );
};
